const ACTION_SELECTOR = [
  "button",
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="image"]',
  "a[href]",
  '[role="button"]',
  '[role="menuitem"]',
  '[data-single-action="true"]',
].join(",");

const RAPID_ACTION_OPT_OUT_SELECTOR = [
  '[data-allow-rapid-action="true"]',
  "label",
  'input[type="file"]',
  'input[type="radio"]',
  'input[type="checkbox"]',
  ".ant-radio-wrapper",
  ".ant-radio-button-wrapper",
  ".ant-checkbox-wrapper",
].join(",");

export const DEFAULT_ACTION_LOCK_MS = 800;

const stopDuplicateAction = (event) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
};

export const installSingleActionGuard = ({
  root = typeof document === "undefined" ? null : document,
  lockMs = DEFAULT_ACTION_LOCK_MS,
} = {}) => {
  if (!root?.addEventListener) return () => {};

  const actionTimestamps = new WeakMap();
  const ownerWindow = root.defaultView || root.ownerDocument?.defaultView;
  const elementConstructor = ownerWindow?.Element;
  const formConstructor = ownerWindow?.HTMLFormElement;

  const isRapidRepeat = (element) => {
    const now = Date.now();
    const previousTimestamp = actionTimestamps.get(element);

    if (
      previousTimestamp !== undefined &&
      now - previousTimestamp < lockMs
    ) {
      return true;
    }

    actionTimestamps.set(element, now);
    return false;
  };

  const allowsRapidAction = (element) =>
    Boolean(element.closest(RAPID_ACTION_OPT_OUT_SELECTOR));

  const findPointerAction = (target) => {
    if (!ownerWindow?.getComputedStyle) return null;

    let currentElement = target;
    let pointerAction = null;

    while (currentElement) {
      if (ownerWindow.getComputedStyle(currentElement).cursor !== "pointer") {
        if (pointerAction) break;
      } else {
        pointerAction = currentElement;
      }

      currentElement = currentElement.parentElement;
    }

    return pointerAction;
  };

  const handleClick = (event) => {
    if (event.defaultPrevented || event.button > 0) return;
    if (!elementConstructor || !(event.target instanceof elementConstructor)) {
      return;
    }
    if (allowsRapidAction(event.target)) return;

    const actionElement =
      event.target.closest(ACTION_SELECTOR) || findPointerAction(event.target);
    if (!actionElement || allowsRapidAction(actionElement)) return;

    if (
      actionElement.disabled ||
      actionElement.getAttribute("aria-disabled") === "true"
    ) {
      return;
    }

    if (isRapidRepeat(actionElement)) stopDuplicateAction(event);
  };

  const handleSubmit = (event) => {
    if (
      event.defaultPrevented ||
      !formConstructor ||
      !(event.target instanceof formConstructor) ||
      allowsRapidAction(event.target)
    ) {
      return;
    }

    if (isRapidRepeat(event.target)) stopDuplicateAction(event);
  };

  root.addEventListener("click", handleClick, true);
  root.addEventListener("submit", handleSubmit, true);

  return () => {
    root.removeEventListener("click", handleClick, true);
    root.removeEventListener("submit", handleSubmit, true);
  };
};
